/**
 * Orbit camera + world→screen projection (DESIGNS-TRACK-3D / Y).
 * dim=2 → identity xy orthographic (camera ignored).
 * dim=3 → yaw/pitch/distance orbit, ortho or perspective.
 *
 * CPU projectParticles is canonical for pick/lenses.
 * buildViewProjMatrix matches that algebra for WebGL MVP (Track Y1).
 */

import type { RenderParams } from "../sim/params";

export type OrbitCamera = {
  yaw: number;
  pitch: number;
  distance: number;
  /** Vertical FOV in degrees; 0 = orthographic */
  fovDeg: number;
};

export const DEFAULT_CAMERA: OrbitCamera = {
  yaw: 0.55,
  pitch: 0.35,
  distance: 3.2,
  fovDeg: 0,
};

export function cameraFromRender(r: RenderParams): OrbitCamera {
  return {
    yaw: r.cameraYaw ?? DEFAULT_CAMERA.yaw,
    pitch: r.cameraPitch ?? DEFAULT_CAMERA.pitch,
    distance: Math.max(0.5, r.cameraDistance ?? DEFAULT_CAMERA.distance),
    fovDeg: Math.max(0, r.cameraFov ?? DEFAULT_CAMERA.fovDeg),
  };
}

const PITCH_MAX = 1.45;

export function clampPitch(p: number): number {
  return Math.max(-PITCH_MAX, Math.min(PITCH_MAX, p));
}

export type ProjectedPoint = {
  x: number;
  y: number;
  depth: number;
  visible: boolean;
};

/** World → eye (view) using the same yaw/pitch/distance as projectParticles. */
export function worldToEye(
  wx: number,
  wy: number,
  wz: number,
  yaw: number,
  pitch: number,
  distance: number,
): { xe: number; ye: number; ze: number } {
  const cosY = Math.cos(yaw);
  const sinY = Math.sin(yaw);
  const cosP = Math.cos(pitch);
  const sinP = Math.sin(pitch);
  // R_y(-yaw)
  const x1 = cosY * wx + sinY * wz;
  const y1 = wy;
  const z1 = -sinY * wx + cosY * wz;
  // R_x(-pitch)
  const xe = x1;
  const ye = cosP * y1 + sinP * z1;
  const z2 = -sinP * y1 + cosP * z1;
  const ze = z2 - distance;
  return { xe, ye, ze };
}

/**
 * Project one world point to CSS pixel coords (same algebra as projectParticles).
 * width/height are CSS pixels, never DPR buffer size.
 */
export function projectWorldPoint(
  wx: number,
  wy: number,
  wz: number,
  render: RenderParams,
  widthCss: number,
  heightCss: number,
  dim: number,
): ProjectedPoint {
  const cx = widthCss / 2;
  const cy = heightCss / 2;
  const range = Math.max(1e-6, render.plotRange);
  const scale = Math.min(widthCss, heightCss) / 2;

  if (dim < 3) {
    const s = scale / range;
    return {
      x: cx + wx * s,
      y: cy - wy * s,
      depth: 0,
      visible: true,
    };
  }

  const cam = cameraFromRender(render);
  const pitch = clampPitch(cam.pitch);
  const { xe, ye, ze } = worldToEye(wx, wy, wz, cam.yaw, pitch, cam.distance);
  const depth = -ze;
  const persp = cam.fovDeg > 1e-3;
  let u: number;
  let v: number;
  let visible = true;
  if (persp) {
    if (ze >= -1e-4) visible = false;
    const z = Math.min(ze, -1e-4);
    const tanHalf = Math.tan((cam.fovDeg * Math.PI) / 180 / 2);
    const f = 1 / (-z * tanHalf);
    u = xe * f;
    v = ye * f;
  } else {
    u = xe / range;
    v = ye / range;
  }
  return {
    x: cx + u * scale,
    y: cy - v * scale,
    depth,
    visible,
  };
}

/**
 * Project all particles into screen pixel coords.
 * screenX/Y length n; depth larger = farther from camera (for sort/cues).
 */
export function projectParticles(
  positions: Float64Array,
  dim: number,
  n: number,
  render: RenderParams,
  width: number,
  height: number,
  outX: Float64Array,
  outY: Float64Array,
  outDepth: Float64Array,
): void {
  const stride = Math.max(1, dim);
  for (let i = 0; i < n; i++) {
    const base = i * stride;
    const wx = positions[base] ?? 0;
    const wy = positions[base + 1] ?? 0;
    const wz = dim >= 3 ? (positions[base + 2] ?? 0) : 0;
    const p = projectWorldPoint(wx, wy, wz, render, width, height, dim);
    outX[i] = p.x;
    outY[i] = p.y;
    outDepth[i] = p.depth;
  }
}

/**
 * Column-major mat4 mapping world → clip so that after perspective divide,
 * NDC maps to the same CSS pixels as projectWorldPoint:
 *   screenX = (nx*0.5+0.5)*w
 *   screenY = (1-(ny*0.5+0.5))*h
 *
 * widthCss/heightCss must match projectParticles (CSS px).
 */
export function buildViewProjMatrix(
  render: RenderParams,
  widthCss: number,
  heightCss: number,
  dim: number,
): Float32Array {
  const w = Math.max(1e-6, widthCss);
  const h = Math.max(1e-6, heightCss);
  const range = Math.max(1e-6, render.plotRange);
  const s = Math.min(w, h) / 2;
  // NDC scale: nx = u * (2s/w), ny = v * (2s/h)
  const sx = (2 * s) / w;
  const sy = (2 * s) / h;

  const out = new Float32Array(16);

  if (dim < 3) {
    // ortho: u=x/R, v=y/R → clip = (x*sx/R, y*sy/R, 0, 1)
    const a = sx / range;
    const b = sy / range;
    // column-major identity-ish scale
    out[0] = a;
    out[5] = b;
    out[10] = 0.001; // tiny z so depth buffer is stable
    out[15] = 1;
    return out;
  }

  const cam = cameraFromRender(render);
  const yaw = cam.yaw;
  const pitch = clampPitch(cam.pitch);
  const cosY = Math.cos(yaw);
  const sinY = Math.sin(yaw);
  const cosP = Math.cos(pitch);
  const sinP = Math.sin(pitch);
  const dist = cam.distance;
  const persp = cam.fovDeg > 1e-3;

  // View = T(0,0,-dist) * Rx(-pitch) * Ry(-yaw)
  // Ry(-yaw):
  //   [ cosY  0  sinY 0]
  //   [ 0     1  0    0]
  //   [-sinY  0  cosY 0]
  // Rx(-pitch):
  //   [1  0     0     0]
  //   [0  cosP  sinP  0]
  //   [0 -sinP  cosP  0]
  // Combined R = Rx * Ry (apply yaw first on column vectors):
  // x' = cosY*x + sinY*z
  // y' = cosP*y + sinP*(-sinY*x + cosY*z) = -sinP*sinY*x + cosP*y + sinP*cosY*z
  // z' = -sinP*y + cosP*(-sinY*x + cosY*z) = -cosP*sinY*x - sinP*y + cosP*cosY*z
  // then ze = z' - dist

  const r00 = cosY;
  const r01 = 0;
  const r02 = sinY;
  const r10 = -sinP * sinY;
  const r11 = cosP;
  const r12 = sinP * cosY;
  const r20 = -cosP * sinY;
  const r21 = -sinP;
  const r22 = cosP * cosY;
  // translation after R: t = (0,0,-dist) in eye; eye = R*world + (0,0,-dist)
  const tx = 0;
  const ty = 0;
  const tz = -dist;

  // Depth map: map ze in [-dist-2, -dist+2] roughly to ndc z; monotonic in -ze
  // For ortho: clip = (xe*sx/R, ye*sy/R, z_ndc, 1) with z_ndc from ze
  // For persp: clip_xy = (xe*sx/tan, ye*sy/tan), clip_w = -ze, clip_z = f(ze)

  // Use a gentle linear depth in eye-z before divide:
  // z_ndc ≈ (ze + dist) / (range*2) mapped — nearer (ze closer to 0 from below) smaller depth
  // ze ≈ -dist at origin. far points more negative ze.
  // We want larger -ze → larger depth buffer.
  // z_clip/w = (A*ze + B) / w
  const near = 0.05;
  const far = Math.max(20, dist + range * 4);
  // Standard GL-ish: z_ndc = (far+near)/(far-near) + 2*far*near/((far-near)*ze) with w=-ze... simplify:
  // Use clip_z = (ze + mid) / half so z_ndc in roughly [-1,1] with w=1 for ortho.
  const mid = -dist;
  const half = Math.max(1e-3, (far - near) / 2);

  if (!persp) {
    // M = P * V where V is view, P scales xy and maps z
    // eye = R * world + T
    // clip_x = (sx/R) * xe, clip_y = (sy/R) * ye, clip_z = (ze - mid)/half * 0.5?, clip_w = 1
    const px = sx / range;
    const py = sy / range;
    const pz = 1 / half; // ze → roughly small ndc
    // clip = P * (R*w + T) = (P*R)*w + P*T
    // out is column-major mat4
    setMat4MulPR(
      out,
      px,
      py,
      pz,
      0,
      r00,
      r01,
      r02,
      r10,
      r11,
      r12,
      r20,
      r21,
      r22,
      tx,
      ty,
      tz,
      mid,
    );
    return out;
  }

  const tanHalf = Math.tan((cam.fovDeg * Math.PI) / 180 / 2);
  // clip_x = xe * sx / tanHalf, clip_y = ye * sy / tanHalf, clip_w = -ze
  // after divide: nx = xe/(-ze)/tan * sx = u*sx. Good.
  // clip_z: use -ze mapped so depth = linear in -ze after divide:
  // clip_z = a * (-ze) + b, with w = -ze → z_ndc = a + b/(-ze)
  // Prefer simpler: clip_z = ze * (something) with w=-ze → z_ndc = -something constant — bad.
  // Use: clip_z = (far+near)/(far-near)*ze + 2*far*near/(far-near) with w=-ze
  // → z_ndc = -((far+near)/(far-near)) - 2*far*near/((far-near)*ze)  ... standard
  // Standard OpenGL-ish perspective depth with w = -ze:
  // clip_z = ((f+n)/(n-f))*ze + (2fn)/(n-f)
  const A = (far + near) / (near - far);
  const B = (2 * far * near) / (near - far);
  const px = sx / tanHalf;
  const py = sy / tanHalf;
  // clip = P * eye, eye = R*world + T
  // P =
  // [px 0  0  0]
  // [0  py 0  0]
  // [0  0  A  B]
  // [0  0 -1  0]
  return mulPerspectiveView(
    out,
    px,
    py,
    A,
    B,
    r00,
    r01,
    r02,
    r10,
    r11,
    r12,
    r20,
    r21,
    r22,
    tx,
    ty,
    tz,
  );
}

/** Ortho path: scale eye xy, linear ze depth, w=1. */
function setMat4MulPR(
  out: Float32Array,
  px: number,
  py: number,
  pz: number,
  _pad: number,
  r00: number,
  r01: number,
  r02: number,
  r10: number,
  r11: number,
  r12: number,
  r20: number,
  r21: number,
  r22: number,
  tx: number,
  ty: number,
  tz: number,
  mid: number,
): void {
  // clip_x = px * xe = px*(r00 x + r01 y + r02 z + tx)
  // clip_y = py * ye
  // clip_z = pz * (ze - mid) = pz*(r20 x + r21 y + r22 z + tz - mid)
  // clip_w = 1
  const tzAdj = tz - mid;
  // column 0
  out[0] = px * r00;
  out[1] = py * r10;
  out[2] = pz * r20;
  out[3] = 0;
  // column 1
  out[4] = px * r01;
  out[5] = py * r11;
  out[6] = pz * r21;
  out[7] = 0;
  // column 2
  out[8] = px * r02;
  out[9] = py * r12;
  out[10] = pz * r22;
  out[11] = 0;
  // column 3
  out[12] = px * tx;
  out[13] = py * ty;
  out[14] = pz * tzAdj;
  out[15] = 1;
}

function mulPerspectiveView(
  out: Float32Array,
  px: number,
  py: number,
  A: number,
  B: number,
  r00: number,
  r01: number,
  r02: number,
  r10: number,
  r11: number,
  r12: number,
  r20: number,
  r21: number,
  r22: number,
  tx: number,
  ty: number,
  tz: number,
): Float32Array {
  // clip_x = px * xe
  // clip_y = py * ye
  // clip_z = A * ze + B
  // clip_w = -ze
  // column 0
  out[0] = px * r00;
  out[1] = py * r10;
  out[2] = A * r20;
  out[3] = -r20;
  // column 1
  out[4] = px * r01;
  out[5] = py * r11;
  out[6] = A * r21;
  out[7] = -r21;
  // column 2
  out[8] = px * r02;
  out[9] = py * r12;
  out[10] = A * r22;
  out[11] = -r22;
  // column 3
  out[12] = px * tx;
  out[13] = py * ty;
  out[14] = A * tz + B;
  out[15] = -tz;
  return out;
}

/** Apply column-major mat4 to vec3 → NDC (after perspective divide). */
export function transformMvpNdc(
  m: Float32Array,
  x: number,
  y: number,
  z: number,
): { nx: number; ny: number; nz: number; w: number } {
  const cx = m[0]! * x + m[4]! * y + m[8]! * z + m[12]!;
  const cy = m[1]! * x + m[5]! * y + m[9]! * z + m[13]!;
  const cz = m[2]! * x + m[6]! * y + m[10]! * z + m[14]!;
  const cw = m[3]! * x + m[7]! * y + m[11]! * z + m[15]!;
  const inv = Math.abs(cw) > 1e-12 ? 1 / cw : 0;
  return { nx: cx * inv, ny: cy * inv, nz: cz * inv, w: cw };
}

/** NDC → CSS pixels (matches projectParticles screen mapping). */
export function ndcToScreen(
  nx: number,
  ny: number,
  widthCss: number,
  heightCss: number,
): { x: number; y: number } {
  return {
    x: (nx * 0.5 + 0.5) * widthCss,
    y: (1 - (ny * 0.5 + 0.5)) * heightCss,
  };
}

/** Unproject a screen pixel to nearest particle — returns index or -1. */
export function pickProjected(
  screenX: Float64Array,
  screenY: Float64Array,
  n: number,
  px: number,
  py: number,
  hitRadiusPx: number,
): number {
  const r2 = hitRadiusPx * hitRadiusPx;
  let best = -1;
  let bestD = r2;
  for (let i = 0; i < n; i++) {
    const dx = screenX[i]! - px;
    const dy = screenY[i]! - py;
    const d2 = dx * dx + dy * dy;
    if (d2 <= bestD) {
      bestD = d2;
      best = i;
    }
  }
  return best;
}

/**
 * Screen CSS pixel → world position for spawn / place tools.
 * dim=2: orthographic inverse of projectWorldPoint.
 * dim=3: ray through pixel, intersect plane through origin facing the camera
 *        (depth of the origin under the current orbit).
 */
export function unprojectScreenToWorld(
  px: number,
  py: number,
  render: RenderParams,
  widthCss: number,
  heightCss: number,
  dim: number,
): { x: number; y: number; z: number } {
  const cx = widthCss / 2;
  const cy = heightCss / 2;
  const range = Math.max(1e-6, render.plotRange);
  const scale = Math.min(widthCss, heightCss) / 2;

  if (dim < 3) {
    const s = scale / range;
    return {
      x: (px - cx) / s,
      y: (cy - py) / s,
      z: 0,
    };
  }

  const cam = cameraFromRender(render);
  const yaw = cam.yaw;
  const pitch = clampPitch(cam.pitch);
  const cosY = Math.cos(yaw);
  const sinY = Math.sin(yaw);
  const cosP = Math.cos(pitch);
  const sinP = Math.sin(pitch);

  // NDC-like u,v matching projectWorldPoint (before * scale / after)
  const u = (px - cx) / scale;
  const v = (cy - py) / scale;

  // Place on the plane through world origin facing the camera:
  // eye coords of origin: worldToEye(0,0,0) → ze = -distance
  const zePlane = -cam.distance;
  let xe: number;
  let ye: number;
  let ze: number;
  if (cam.fovDeg > 1e-3) {
    const tanHalf = Math.tan((cam.fovDeg * Math.PI) / 180 / 2);
    // project: u = xe / (-ze * tanHalf) with ze < 0
    const f = 1 / (-zePlane * tanHalf);
    xe = u / f;
    ye = v / f;
    ze = zePlane;
  } else {
    xe = u * range;
    ye = v * range;
    ze = zePlane;
  }

  // Inverse of worldToEye: eye = Rx(-p)*Ry(-y)*world + (0,0,-dist)
  // so R*world = (xe, ye, ze+dist); world = R^T * that
  const x2 = xe;
  const y2 = ye;
  const z2 = ze + cam.distance;
  // R = Rx(-pitch) * Ry(-yaw); R^T = Ry(yaw) * Rx(pitch)
  // Rx(pitch):
  const y1 = cosP * y2 - sinP * z2;
  const z1 = sinP * y2 + cosP * z2;
  const x1 = x2;
  // Ry(yaw):
  const wx = cosY * x1 - sinY * z1;
  const wy = y1;
  const wz = sinY * x1 + cosY * z1;
  return { x: wx, y: wy, z: wz };
}
